import { useAppointmentAvailableResources } from "@/components/booking/multiProviderAppointmentDrawer/useAppointmentAvailableResources";
import { useConfirm } from "@/components/providers/confirmProvider";

type AppointmentService = {
  serviceMenuItem: { id: string };
  startTime: Date;
  endTime: Date;
};

/**
 * Returns a wrapper function that checks if appointment services are overbooking resources.
 * If they are, it will show a confirm dialog to the user.
 * If the user confirms, it will call the action function.
 *
 * Used for creating multi-provider appointments.
 */
export const useConfirmResourcesOverbooking = ({
  appointmentServices,
}: {
  appointmentServices: AppointmentService[];
}) => {
  const { getConfirm } = useConfirm();

  const resources = useAppointmentAvailableResources({ appointmentServices });

  const confirmResourceOverbooking = async (action: () => Promise<void>) => {
    const overbookedResources = [];
    if (
      resources?.services?.some(
        (service) =>
          service.rooms?.availableRooms === 0 && service.rooms?.isRoomRequired
      )
    ) {
      overbookedResources.push("room");
    }

    if (
      resources?.services?.some((service) =>
        service.devices?.some((device) => device?.availableDevices === 0)
      )
    ) {
      overbookedResources.push("device");
    }

    if (overbookedResources.length > 0) {
      const overbookedResourcesString = overbookedResources.join(" and ");

      const ok = await getConfirm({
        title: `You are overbooking a ${overbookedResourcesString}`,
        description: `You are double booking a ${overbookedResourcesString} with this appointment. Are you sure you want to continue with this booking?`,
        discardButtonText: "No, go back",
        confirmButtonText: "Confirm booking",
      });

      if (!ok) return;
    }
    await action();
  };

  return confirmResourceOverbooking;
};
